import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Checkbox,
  Avatar,
  Stack,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FilterListIcon from "@mui/icons-material/FilterList";

export interface MealItem {
  _id: string;
  name: string;
  image?: string;
  currentPrice: number;
  categoryId?: { _id: string; name: string } | string;
  tags?: string[];
  isVegan?: boolean;
  isGlutenFree?: boolean;
}

interface MealMultiSelectProps {
  meals: MealItem[];
  selectedMealIds: string[];
  onChange: (selectedIds: string[]) => void;
  maxHeight?: number | string;
}

export const MealMultiSelect: React.FC<MealMultiSelectProps> = ({
  meals,
  selectedMealIds,
  onChange,
  maxHeight = 340,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  // Extract unique category names from meals list
  const categories = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();
    meals.forEach((m) => {
      const catName =
        typeof m.categoryId === "object" && m.categoryId?.name
          ? m.categoryId.name
          : "General";
      const catId =
        typeof m.categoryId === "object" && m.categoryId?._id
          ? m.categoryId._id
          : catName;

      if (!map.has(catName)) {
        map.set(catName, { id: catId, name: catName, count: 0 });
      }
      map.get(catName)!.count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [meals]);

  // Filtered meals based on Search Query and Category
  const filteredMeals = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return meals.filter((meal) => {
      // Category filter
      if (selectedCategory !== "ALL") {
        const catName =
          typeof meal.categoryId === "object" && meal.categoryId?.name
            ? meal.categoryId.name
            : "General";
        if (catName !== selectedCategory) return false;
      }

      // Search query filter
      if (!q) return true;
      const matchName = meal.name.toLowerCase().includes(q);
      const matchTags = meal.tags?.some((t) => t.toLowerCase().includes(q));
      const catName =
        typeof meal.categoryId === "object" && meal.categoryId?.name
          ? meal.categoryId.name.toLowerCase()
          : "";
      const matchCat = catName.includes(q);

      return matchName || matchTags || matchCat;
    });
  }, [meals, searchQuery, selectedCategory]);

  // Lookup map for fast selected check
  const selectedSet = useMemo(() => new Set(selectedMealIds), [selectedMealIds]);

  // Toggle single meal
  const handleToggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selectedMealIds.filter((item) => item !== id));
    } else {
      onChange([...selectedMealIds, id]);
    }
  };

  // Select all visible (filtered) meals
  const handleSelectAllFiltered = () => {
    const newSet = new Set(selectedMealIds);
    filteredMeals.forEach((m) => newSet.add(m._id));
    onChange(Array.from(newSet));
  };

  // Deselect all visible (filtered) meals
  const handleDeselectFiltered = () => {
    const filteredSet = new Set(filteredMeals.map((m) => m._id));
    onChange(selectedMealIds.filter((id) => !filteredSet.has(id)));
  };

  // Clear all selections
  const handleClearAll = () => {
    onChange([]);
  };

  // Selected meals objects for chip preview
  const selectedMealObjects = useMemo(() => {
    const map = new Map(meals.map((m) => [m._id, m]));
    return selectedMealIds.map((id) => map.get(id)).filter(Boolean) as MealItem[];
  }, [meals, selectedMealIds]);

  const allFilteredSelected =
    filteredMeals.length > 0 &&
    filteredMeals.every((m) => selectedSet.has(m._id));

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        bgcolor: "#ffffff",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      {/* Top Search & Filter Bar */}
      <Box sx={{ p: 1.75, pb: 1, bgcolor: "#f8fafc", borderBottom: "1px solid", borderColor: "divider" }}>
        <TextField
          size="small"
          fullWidth
          placeholder={`Search ${meals.length} dishes by name, tag, or category...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            bgcolor: "#ffffff",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        {/* Category Pills Strip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            overflowX: "auto",
            pt: 1.25,
            pb: 0.5,
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.1)", borderRadius: 2 },
          }}
        >
          <FilterListIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, mr: 0.25 }} />
          <Chip
            label={`All Dishes (${meals.length})`}
            size="small"
            clickable
            onClick={() => setSelectedCategory("ALL")}
            color={selectedCategory === "ALL" ? "primary" : "default"}
            variant={selectedCategory === "ALL" ? "filled" : "outlined"}
            sx={{
              fontWeight: selectedCategory === "ALL" ? 700 : 500,
              fontSize: 11.5,
              flexShrink: 0,
            }}
          />
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat.name;
            return (
              <Chip
                key={cat.id}
                label={`${cat.name} (${cat.count})`}
                size="small"
                clickable
                onClick={() => setSelectedCategory(cat.name)}
                color={isSelected ? "primary" : "default"}
                variant={isSelected ? "filled" : "outlined"}
                sx={{
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: 11.5,
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Metrics & Bulk Action Controls */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#f1f5f9",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color={selectedMealIds.length > 0 ? "primary" : "default"}
            label={`${selectedMealIds.length} of ${meals.length} selected`}
            sx={{ fontWeight: 700, fontSize: 11.5 }}
          />
          {filteredMeals.length !== meals.length && (
            <Typography variant="caption" color="text.secondary">
              (Showing {filteredMeals.length} filtered)
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          {allFilteredSelected ? (
            <Button
              size="small"
              startIcon={<RemoveDoneIcon sx={{ fontSize: 16 }} />}
              onClick={handleDeselectFiltered}
              sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
              color="inherit"
            >
              Deselect Filtered
            </Button>
          ) : (
            <Button
              size="small"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={handleSelectAllFiltered}
              sx={{ textTransform: "none", fontSize: 12, py: 0.25, fontWeight: 600 }}
              color="primary"
            >
              Select All Filtered ({filteredMeals.length})
            </Button>
          )}

          {selectedMealIds.length > 0 && (
            <Button
              size="small"
              onClick={handleClearAll}
              color="inherit"
              sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
            >
              Clear All
            </Button>
          )}
        </Stack>
      </Box>

      {/* Selected Items Tray (Horizontal Preview) */}
      {selectedMealObjects.length > 0 && (
        <Box
          sx={{
            p: 1,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            overflowX: "auto",
            bgcolor: "#fff7ed",
            borderBottom: "1px solid",
            borderColor: "rgba(249, 115, 22, 0.2)",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(249, 115, 22, 0.3)", borderRadius: 2 },
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "primary.main", flexShrink: 0, mr: 0.5 }}
          >
            Selected:
          </Typography>
          {selectedMealObjects.map((meal) => (
            <Chip
              key={meal._id}
              avatar={
                meal.image ? (
                  <Avatar src={meal.image} alt={meal.name} />
                ) : (
                  <Avatar sx={{ bgcolor: "primary.light" }}>
                    <RestaurantMenuIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                )
              }
              label={meal.name}
              size="small"
              onDelete={() => handleToggle(meal._id)}
              color="primary"
              variant="filled"
              sx={{
                flexShrink: 0,
                fontSize: 11.5,
                fontWeight: 600,
                maxWidth: 220,
              }}
            />
          ))}
        </Box>
      )}

      {/* Dishes Scrollable Grid / List */}
      <Box
        sx={{
          p: 1.5,
          maxHeight,
          overflowY: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1.25,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.15)", borderRadius: 3 },
        }}
      >
        {filteredMeals.length === 0 ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              py: 6,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2" fontWeight={600}>
              No dishes found
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Try searching with another keyword or resetting the category filter.
            </Typography>
            {(searchQuery || selectedCategory !== "ALL") && (
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1.5, textTransform: "none", fontSize: 12 }}
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("ALL");
                }}
              >
                Reset Filters
              </Button>
            )}
          </Box>
        ) : (
          filteredMeals.map((meal) => {
            const isSelected = selectedSet.has(meal._id);
            const categoryName =
              typeof meal.categoryId === "object" && meal.categoryId?.name
                ? meal.categoryId.name
                : "";

            return (
              <Box
                key={meal._id}
                onClick={() => handleToggle(meal._id)}
                sx={{
                  display: "flex",
                  minWidth: 0,
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.2,
                  borderRadius: 2,
                  cursor: "pointer",
                  border: "1.5px solid",
                  borderColor: isSelected ? "primary.main" : "#e2e8f0",
                  bgcolor: isSelected ? "rgba(249, 115, 22, 0.05)" : "#ffffff",
                  boxShadow: isSelected
                    ? "0 2px 8px rgba(249, 115, 22, 0.12)"
                    : "0 1px 2px rgba(0,0,0,0.02)",
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    transform: "translateY(-1px)",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
                  },
                }}
              >
                {/* Checkbox */}
                <Checkbox
                  size="small"
                  checked={isSelected}
                  color="primary"
                  sx={{ p: 0.25, color: isSelected ? "primary.main" : "#cbd5e1" }}
                />

                {/* Dish Thumbnail */}
                {meal.image ? (
                  <Box
                    component="img"
                    src={meal.image}
                    alt={meal.name}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      objectFit: "cover",
                      flexShrink: 0,
                      border: "1px solid #e2e8f0",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <RestaurantMenuIcon sx={{ fontSize: 22, color: "#94a3b8" }} />
                  </Box>
                )}

                {/* Meal Details */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 700 : 600,
                      color: isSelected ? "primary.dark" : "text.primary",
                      fontSize: 13,
                      whiteSpace: { xs: "normal", sm: "nowrap" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {meal.name}
                  </Typography>

                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                    {categoryName && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 11,
                          color: "text.secondary",
                          bgcolor: "#f1f5f9",
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.75,
                        }}
                      >
                        {categoryName}
                      </Typography>
                    )}
                    {meal.isVegan && (
                      <Tooltip title="Vegan">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#22c55e",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                {/* Price Pill */}
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 1.25,
                    bgcolor: isSelected ? "rgba(249, 115, 22, 0.15)" : "#f8fafc",
                    border: "1px solid",
                    borderColor: isSelected ? "primary.light" : "#e2e8f0",
                    color: isSelected ? "primary.dark" : "#334155",
                    fontWeight: 700,
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  ₹{meal.currentPrice}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};
